/**
 * Generic "Let's Talk" booking page.
 *
 * Route: /lets-talk/:programSlug
 *
 * Data sources:
 *   GET /api/public/programs/:slug  → program info + resolved tokens + discoveryCall config
 *   GET /api/public/people/:slug    → host person (photo, name, role, bio, bookingUrls)
 *
 * On load, writes CSS custom properties to <html> from the resolved tokens so
 * every element can use var(--prog-primary), var(--font-heading), etc. Google Fonts
 * is loaded via the precomputed googleFontsUrl from the API.
 */

import { useEffect, useMemo, useRef, useState } from 'react';
import { useParams } from 'react-router-dom';
import { BRAND_NAME, BRAND_HOME_URL, SUPPORT_EMAIL } from './branding';
import { recordLandingView } from './api';
import BrokenPage from './components/BrokenPage';
import { PolicyLinks } from './components/PolicyLinks';
import { fetchWithRetry } from './lib/fetchRetry';
import { NativeBookingPicker } from './components/NativeBookingPicker';
import { useBrandGtm } from './lib/useBrandGtm';
import { useNoIndex } from './lib/useNoIndex';
import { STAGED_SLUGS } from './lib/launchGates';

const API = import.meta.env.VITE_API_BASE_URL as string;

// ────────────────────────────────────────────────────────────────────────────
// Types (mirroring worker/src/brands.ts public shapes)
// ────────────────────────────────────────────────────────────────────────────

interface ProgramRecord {
  slug: string;
  name: string;
  type: string;
}

interface BrandInfo {
  id: string;
  slug: string;
  name: string;
  tagline: string | null;
  palette: Record<string, string>;
  fonts: Record<string, unknown>;
  design_tokens: Record<string, unknown> | null;
  logoUrl: string | null;
  logoWhiteUrl: string | null;
  faviconUrl: string | null;
  gtmContainerId?: string | null;
}

interface ResolvedTokens {
  palette: Record<string, string> | null;
  fonts: Record<string, unknown> | null;
  designTokens: Record<string, unknown> | null;
  designTreatments: { hero?: string; ctaOnLight?: string } | null;
}

interface DiscoveryCallConfig {
  hostSlug: string;
  bookingKey: string;
  duration?: string;
  format?: string;
  cost?: string;
  headline?: string;
  subhead?: string;
  trustQuote?: string;
  trustQuoteAttribution?: string;
  expectations?: string[];
  enrollUrl?: string;
  enrollLabel?: string;
}

interface ProgramPublicResponse {
  program: ProgramRecord;
  brand: BrandInfo | null;
  tokens: ResolvedTokens;
  discoveryCall: DiscoveryCallConfig | null;
  googleFontsUrl: string | null;
}

interface PersonPublic {
  slug: string;
  name: string;
  role: string | null;
  bio: string | null;
  first_name: string | null;
  last_name: string | null;
  booking_url: string | null;
  bookingUrls: Record<string, string>;
  linkedin_url: string | null;
  photoUrl: string | null;
}

// ────────────────────────────────────────────────────────────────────────────
// Token helpers
// ────────────────────────────────────────────────────────────────────────────

const CSS_PROP_CLEANUP: string[] = [];

function applyTokensToDom(data: ProgramPublicResponse) {
  const root = document.documentElement;
  const { palette, fonts, designTokens } = data.tokens;

  const set = (prop: string, val: string | null | undefined) => {
    if (val) {
      root.style.setProperty(prop, val);
      CSS_PROP_CLEANUP.push(prop);
    }
  };

  // Program palette
  if (palette) {
    set('--prog-text-dark', palette.textDark);
    set('--prog-deep', palette.deep ?? palette.textDark);
    set('--prog-primary', palette.primary);
    set('--prog-bright', palette.bright ?? palette.primary);
    set('--prog-tint', palette.tint);
    set('--prog-canvas', palette.canvas);
    set('--prog-highlight', palette.highlight ?? null);
  }

  // Brand palette (cta + secondary)
  const bpal = data.brand?.palette;
  if (bpal) {
    set('--brand-primary', bpal.primary);
    set('--brand-cta', bpal.cta);
    set('--brand-canvas', bpal.canvas);
  }

  // Fonts
  const heading = (fonts as any)?.heading as { family?: string } | undefined;
  const body    = (fonts as any)?.body    as { family?: string } | undefined;
  const display = (fonts as any)?.display as { family?: string } | undefined;
  set('--font-heading', heading?.family ? `'${heading.family}', sans-serif` : null);
  set('--font-body',    body?.family    ? `'${body.family}', sans-serif`    : null);
  set('--font-display', display?.family ? `'${display.family}', serif`      : null);

  // Design tokens
  const radius = (designTokens as any)?.radius as Record<string, number> | undefined;
  const page   = (designTokens as any)?.page   as { maxWidth?: number } | undefined;
  if (radius?.button  != null) set('--radius-button',    `${radius.button}px`);
  if (radius?.card    != null) set('--radius-card',      `${radius.card}px`);
  if (radius?.container != null) set('--radius-container', `${radius.container}px`);
  if (page?.maxWidth  != null) set('--page-max-width',   `${page.maxWidth}px`);
}

function cleanupTokensFromDom() {
  const root = document.documentElement;
  while (CSS_PROP_CLEANUP.length) {
    root.style.removeProperty(CSS_PROP_CLEANUP.pop()!);
  }
}

// ────────────────────────────────────────────────────────────────────────────
// Main component
// ────────────────────────────────────────────────────────────────────────────

export default function LetsTalkPage() {
  const { programSlug = '' } = useParams<{ programSlug: string }>();
  useNoIndex(STAGED_SLUGS.has(programSlug));

  // Contact prefill — read once from URL params (injected by email {{discoveryCallUrl}}).
  // fn=first, ln=last, em=email. Used to pre-fill the HubSpot scheduling form so
  // returning registrants don't have to re-type their details.
  const prefill = useMemo(() => {
    const p = new URLSearchParams(window.location.search);
    return {
      firstName: p.get('fn') ?? '',
      lastName:  p.get('ln') ?? '',
      email:     p.get('em') ?? '',
    };
  }, []);

  const [status, setStatus] = useState<'loading' | 'ready' | 'not-found' | 'error'>('loading');
  const [data, setData] = useState<ProgramPublicResponse | null>(null);
  const [host, setHost] = useState<PersonPublic | null>(null);
  const embedRef = useRef<HTMLDivElement | null>(null);
  // Native-first: try the engine's own booking picker; a 404 (no active native
  // type for this host+key) falls back to the HubSpot embed below. Each
  // discovery-call link flips to native when its booking_types row is
  // activated — no code change on this page.
  const [nativeMode, setNativeMode] = useState<'try' | 'fallback'>('try');

  // ── 0. Landing-views beacon — fires once per mount for funnel tracking. ──
  useEffect(() => {
    if (programSlug) recordLandingView(programSlug);
  }, [programSlug]);

  // Brand-aware GTM — discovery-call landings are a tracked funnel step.
  useBrandGtm(data?.brand?.gtmContainerId, status !== 'loading');

  // ── 1. Fetch program data ─────────────────────────────────────────────────
  useEffect(() => {
    if (!programSlug) { setStatus('not-found'); return; }
    let cancelled = false;
    (async () => {
      try {
        const res = await fetchWithRetry(`${API}/api/public/programs/${encodeURIComponent(programSlug)}`);
        if (!res.ok) {
          if (!cancelled) setStatus(res.status === 404 ? 'not-found' : 'error');
          return;
        }
        const json = await res.json() as ProgramPublicResponse;
        if (!cancelled) {
          setData(json);
          setStatus('ready');
        }
      } catch {
        if (!cancelled) setStatus('error');
      }
    })();
    return () => { cancelled = true; };
  }, [programSlug]);

  // ── 2. Apply tokens to <html> ─────────────────────────────────────────────
  useEffect(() => {
    if (!data) return;
    applyTokensToDom(data);
    return cleanupTokensFromDom;
  }, [data]);

  // ── 3. Load Google Fonts ──────────────────────────────────────────────────
  useEffect(() => {
    if (!data?.googleFontsUrl) return;
    const link = document.createElement('link');
    link.rel = 'stylesheet';
    link.href = data.googleFontsUrl;
    document.head.appendChild(link);
    return () => { link.remove(); };
  }, [data?.googleFontsUrl]);

  // ── 4. Fetch host person ──────────────────────────────────────────────────
  useEffect(() => {
    const hostSlug = data?.discoveryCall?.hostSlug;
    if (!hostSlug) return;
    let cancelled = false;
    (async () => {
      try {
        const res = await fetch(`${API}/api/public/people/${encodeURIComponent(hostSlug)}`);
        if (!res.ok || cancelled) return;
        const json = await res.json() as { person: PersonPublic };
        if (!cancelled) setHost(json.person);
      } catch { /* host photo is non-critical — degrade gracefully */ }
    })();
    return () => { cancelled = true; };
  }, [data?.discoveryCall?.hostSlug]);

  // ── 5. Document title ─────────────────────────────────────────────────────
  useEffect(() => {
    if (data?.program?.name) {
      document.title = `Let's Talk — ${data.program.name} · ${BRAND_NAME}`;
    }
    return () => { document.title = BRAND_NAME; };
  }, [data?.program?.name]);

  // ── 5b. Brand favicon ─────────────────────────────────────────────────────
  // Swap <link rel="icon"> to the brand's favicon when one is configured. On
  // unmount, restore the engine default so other routes don't inherit a stale
  // brand icon. Targets the favicon link by selector instead of by id so it
  // works whether the static index.html shipped one or not.
  useEffect(() => {
    const faviconUrl = data?.brand?.faviconUrl;
    if (!faviconUrl) return;
    const head = document.head;
    let icon = head.querySelector<HTMLLinkElement>('link[rel="icon"]');
    const previousHref = icon?.href ?? null;
    if (!icon) {
      icon = document.createElement('link');
      icon.rel = 'icon';
      head.appendChild(icon);
    }
    icon.href = faviconUrl;
    return () => {
      const current = head.querySelector<HTMLLinkElement>('link[rel="icon"]');
      if (current) current.href = previousHref ?? '/favicon.svg';
    };
  }, [data?.brand?.faviconUrl]);

  // ── 6. HubSpot calendar embed ─────────────────────────────────────────────
  const bookingUrl = (() => {
    if (!host || !data?.discoveryCall) return null;
    const { bookingKey } = data.discoveryCall;
    return (bookingKey ? host.bookingUrls?.[bookingKey] : null) ?? host.booking_url;
  })();

  useEffect(() => {
    const container = embedRef.current;
    // The container div only mounts once the native probe has fallen back;
    // nativeMode is a dep so the embed injects the moment that happens.
    if (nativeMode !== 'fallback' || !bookingUrl || !container) return;

    while (container.firstChild) container.removeChild(container.firstChild);

    // Base URL with embed flag
    const base = bookingUrl.includes('?')
      ? `${bookingUrl}&embed=true`
      : `${bookingUrl}?embed=true`;

    // Append contact prefill so HubSpot pre-fills First/Last/Email fields.
    // Params come from the email {{discoveryCallUrl}} which injects fn/ln/em.
    const prefillParts: string[] = [];
    if (prefill.firstName) prefillParts.push(`firstName=${encodeURIComponent(prefill.firstName)}`);
    if (prefill.lastName)  prefillParts.push(`lastName=${encodeURIComponent(prefill.lastName)}`);
    if (prefill.email)     prefillParts.push(`email=${encodeURIComponent(prefill.email)}`);
    const embedSrc = prefillParts.length ? `${base}&${prefillParts.join('&')}` : base;

    const meetingsDiv = document.createElement('div');
    meetingsDiv.className = 'meetings-iframe-container';
    meetingsDiv.setAttribute('data-src', embedSrc);
    meetingsDiv.style.minHeight = '660px';
    container.appendChild(meetingsDiv);

    const SCRIPT_ID = 'hs-meetings-embed-lt';
    const existing = document.getElementById(SCRIPT_ID);
    if (existing) existing.remove();

    const script = document.createElement('script');
    script.id = SCRIPT_ID;
    script.type = 'text/javascript';
    script.src = 'https://static.hsappstatic.net/MeetingsEmbed/ex/MeetingsEmbedCode.js';
    document.head.appendChild(script);

    return () => { while (container.firstChild) container.removeChild(container.firstChild); };
  }, [nativeMode, bookingUrl, prefill]);

  // ── Render states ─────────────────────────────────────────────────────────
  if (status === 'loading') return <Shell><LoadingSpinner /></Shell>;
  if (status === 'not-found') return <BrokenPage variant="404" />;
  if (status === 'error')     return <BrokenPage variant="error" />;

  const dc = data!.discoveryCall;

  // When there's no discoveryCall config, render a graceful fallback
  if (!dc) {
    return (
      <Shell data={data!}>
        <ErrorCard
          title={data!.program.name}
          body="Booking isn't configured for this program yet. Please check back soon."
        />
      </Shell>
    );
  }

  const firstName = host?.first_name ?? host?.name?.split(' ')[0] ?? 'Your Coach';

  return (
    <Shell data={data!}>
      {/* ── Hero ── */}
      <section style={heroStyle}>
        <div style={heroInnerStyle}>
          <h1 style={heroH1Style}>
            {dc.headline ?? `Book a free call with ${firstName}`}
          </h1>
          {dc.subhead && <p style={heroSubStyle}>{dc.subhead}</p>}
          <div style={metaPillsStyle}>
            {dc.duration && <MetaPill icon="⏱" label={dc.duration} />}
            {dc.format   && <MetaPill icon="🎥" label={dc.format}   />}
            {dc.cost     && <MetaPill icon="✓"  label={dc.cost}     />}
          </div>
        </div>
      </section>

      {/* ── Content shell ── */}
      <main style={contentStyle}>

        {/* ── Host + calendar ── */}
        <div style={twoColStyle}>
          {/* Host card */}
          <aside style={hostCardStyle}>
            {host?.photoUrl && (
              <img
                src={host.photoUrl}
                alt={host.name}
                style={hostPhotoStyle}
              />
            )}
            <div style={hostTextStyle}>
              <p style={hostNameStyle}>{host?.name ?? firstName}</p>
              {host?.role && <p style={hostRoleStyle}>{host.role}</p>}
              {host?.bio  && <p style={hostBioStyle}>{host.bio}</p>}
            </div>

            {/* Meta on the host card (mobile-friendly) */}
            <div style={hostMetaStyle}>
              {dc.duration && (
                <div style={hostMetaRowStyle}>
                  <span style={hostMetaIconStyle}>⏱</span>
                  <span style={hostMetaLabelStyle}>{dc.duration}</span>
                </div>
              )}
              {dc.format && (
                <div style={hostMetaRowStyle}>
                  <span style={hostMetaIconStyle}>🎥</span>
                  <span style={hostMetaLabelStyle}>{dc.format}</span>
                </div>
              )}
              {dc.cost && (
                <div style={hostMetaRowStyle}>
                  <span style={hostMetaIconStyle}>✓</span>
                  <span style={hostMetaLabelStyle}>{dc.cost}</span>
                </div>
              )}
            </div>
          </aside>

          {/* Calendar — native picker first, HubSpot embed as fallback */}
          <div style={calendarWrapStyle}>
            {nativeMode === 'try' && dc.hostSlug && dc.bookingKey ? (
              <NativeBookingPicker
                personSlug={dc.hostSlug}
                bookingKey={dc.bookingKey}
                prefill={prefill}
                onFallback={() => setNativeMode('fallback')}
              />
            ) : bookingUrl
              ? <div ref={embedRef} style={{ width: '100%', minHeight: 660 }} />
              : <CalendarPlaceholder hostName={host?.name ?? firstName} />
            }
          </div>
        </div>

        {/* ── What to expect ── */}
        {dc.expectations && dc.expectations.length > 0 && (
          <section style={expectsSection}>
            <h2 style={sectionHeadingStyle}>What we'll cover</h2>
            <ol style={expectsListStyle}>
              {dc.expectations.map((item, i) => (
                <li key={i} style={expectsItemStyle}>
                  <span style={expectsNumberStyle}>{i + 1}</span>
                  <span style={expectsTextStyle}>{item}</span>
                </li>
              ))}
            </ol>
          </section>
        )}

        {/* ── Trust quote ── */}
        {dc.trustQuote && (
          <section style={quoteSection}>
            <blockquote style={quoteStyle}>
              <span style={quoteMarkStyle}>"</span>
              {dc.trustQuote}
              <span style={quoteMarkStyle}>"</span>
            </blockquote>
            {dc.trustQuoteAttribution && (
              <p style={quoteAttrStyle}>— {dc.trustQuoteAttribution}</p>
            )}
          </section>
        )}

        {/* ── Enroll shortcut ── */}
        {dc.enrollUrl && (
          <section style={enrollSection}>
            <p style={enrollTextStyle}>Already decided?</p>
            <a href={dc.enrollUrl} style={enrollCtaStyle}>
              {dc.enrollLabel ?? `Go straight to ${data!.program.name}`} →
            </a>
          </section>
        )}
      </main>

      <PageFooter />
    </Shell>
  );
}

// ────────────────────────────────────────────────────────────────────────────
// Sub-components
// ────────────────────────────────────────────────────────────────────────────

function Shell({ children, data }: { children: React.ReactNode; data?: ProgramPublicResponse | null }) {
  return (
    <div style={pageWrapStyle} data-lets-talk>
      <TopBar data={data} />
      {children}
    </div>
  );
}

function TopBar({ data }: { data?: ProgramPublicResponse | null }) {
  const logoUrl = data?.brand?.logoUrl ?? null;
  return (
    <div style={topBarStyle}>
      <div style={topBarInnerStyle}>
        <a href={BRAND_HOME_URL} style={wordmarkStyle} aria-label={BRAND_NAME}>
          {logoUrl
            ? <img src={logoUrl} alt={BRAND_NAME} style={logoImgStyle} />
            : <span style={wordmarkTextStyle}>{BRAND_NAME}</span>}
        </a>
      </div>
    </div>
  );
}

function MetaPill({ icon, label }: { icon: string; label: string }) {
  return (
    <span style={metaPillStyle}>
      <span style={{ marginRight: 5, opacity: 0.85 }}>{icon}</span>
      {label}
    </span>
  );
}

function LoadingSpinner() {
  return (
    <div style={loadingStyle}>
      <div style={spinnerStyle} />
    </div>
  );
}

function ErrorCard({ title, body }: { title: string; body: string }) {
  return (
    <div style={errorWrapStyle}>
      <div style={errorCardStyle}>
        <h1 style={errorTitleStyle}>{title}</h1>
        <p style={errorBodyStyle}>{body}</p>
        <a href={BRAND_HOME_URL} style={errorCtaStyle}>Back to {BRAND_NAME}</a>
      </div>
    </div>
  );
}

function CalendarPlaceholder({ hostName }: { hostName: string }) {
  return (
    <div style={calPlaceholderStyle}>
      <p style={calPlaceholderTextStyle}>
        Calendar for {hostName} isn't configured yet.
        <br />
        <a href={`mailto:${SUPPORT_EMAIL}`} style={{ color: 'inherit', opacity: 0.7 }}>
          Contact us to book directly.
        </a>
      </p>
    </div>
  );
}

function PageFooter() {
  return (
    <footer style={footerStyle}>
      <a href={BRAND_HOME_URL} style={footLinkStyle}>{BRAND_NAME}</a>
      {' · '}
      <a href={`mailto:${SUPPORT_EMAIL}`} style={footLinkStyle}>{SUPPORT_EMAIL}</a>
      <div style={{ marginTop: 8 }}><PolicyLinks /></div>
    </footer>
  );
}

// ────────────────────────────────────────────────────────────────────────────
// Styles — use CSS custom props written by applyTokensToDom()
//          with safe fallbacks for pre-token render
// ────────────────────────────────────────────────────────────────────────────

const v = (prop: string, fallback: string) =>
  `var(${prop}, ${fallback})` as React.CSSProperties[keyof React.CSSProperties];

const pageWrapStyle: React.CSSProperties = {
  minHeight: '100vh',
  display: 'flex',
  flexDirection: 'column',
  background: v('--prog-canvas', '#f9fafb') as string,
  fontFamily: v('--font-body', "'Poppins', sans-serif") as string,
  color: v('--prog-text-dark', '#111827') as string,
};

const topBarStyle: React.CSSProperties = {
  background: '#ffffff',
  padding: '14px 24px',
  borderBottom: '1px solid #E8EDF2',
};

const topBarInnerStyle: React.CSSProperties = {
  maxWidth: v('--page-max-width', '800px') as string,
  margin: '0 auto',
};

const wordmarkStyle: React.CSSProperties = {
  textDecoration: 'none',
  display: 'inline-flex',
  alignItems: 'center',
};

const logoImgStyle: React.CSSProperties = {
  height: 32,
  display: 'block',
};

const wordmarkTextStyle: React.CSSProperties = {
  fontSize: 15,
  fontWeight: 700,
  color: '#0F172A',
  letterSpacing: '-0.01em',
  fontFamily: v('--font-heading', "'Poppins', sans-serif") as string,
};

const heroStyle: React.CSSProperties = {
  background: `linear-gradient(135deg, ${v('--prog-deep', '#003D32') as string} 0%, ${v('--prog-text-dark', '#002B22') as string} 100%)`,
  padding: '64px 24px 56px',
  color: '#ffffff',
};

const heroInnerStyle: React.CSSProperties = {
  maxWidth: v('--page-max-width', '800px') as string,
  margin: '0 auto',
};

const heroH1Style: React.CSSProperties = {
  margin: '0 0 16px',
  fontSize: 'clamp(28px, 5vw, 40px)',
  fontWeight: 700,
  lineHeight: 1.2,
  letterSpacing: '-0.02em',
  fontFamily: v('--font-heading', "'Poppins', sans-serif") as string,
};

const heroSubStyle: React.CSSProperties = {
  margin: '0 0 28px',
  fontSize: 18,
  lineHeight: 1.55,
  opacity: 0.88,
  maxWidth: 560,
};

const metaPillsStyle: React.CSSProperties = {
  display: 'flex',
  flexWrap: 'wrap',
  gap: 10,
};

const metaPillStyle: React.CSSProperties = {
  display: 'inline-flex',
  alignItems: 'center',
  background: 'rgba(255,255,255,0.15)',
  borderRadius: 100,
  padding: '6px 14px',
  fontSize: 13,
  fontWeight: 500,
  color: '#ffffff',
  backdropFilter: 'blur(4px)',
  border: '1px solid rgba(255,255,255,0.2)',
};

const contentStyle: React.CSSProperties = {
  maxWidth: v('--page-max-width', '800px') as string,
  margin: '0 auto',
  padding: '48px 24px 64px',
  flex: 1,
  width: '100%',
  boxSizing: 'border-box',
};

const twoColStyle: React.CSSProperties = {
  display: 'flex',
  gap: 28,
  alignItems: 'flex-start',
  flexWrap: 'wrap',
  marginBottom: 48,
};

const hostCardStyle: React.CSSProperties = {
  background: '#ffffff',
  borderRadius: v('--radius-card', '22px') as string,
  padding: '28px 22px',
  width: 220,
  flexShrink: 0,
  boxShadow: '0 2px 8px rgba(0,0,0,0.06), 0 8px 24px rgba(0,0,0,0.04)',
  textAlign: 'center',
};

const hostPhotoStyle: React.CSSProperties = {
  width: 88,
  height: 88,
  borderRadius: '50%',
  objectFit: 'cover',
  display: 'block',
  margin: '0 auto 14px',
  border: `3px solid ${v('--prog-primary', '#00C896') as string}`,
};

const hostTextStyle: React.CSSProperties = {
  marginBottom: 20,
};

const hostNameStyle: React.CSSProperties = {
  margin: '0 0 3px',
  fontSize: 16,
  fontWeight: 700,
  color: v('--prog-text-dark', '#111827') as string,
  fontFamily: v('--font-heading', "'Poppins', sans-serif") as string,
};

const hostRoleStyle: React.CSSProperties = {
  margin: '0 0 10px',
  fontSize: 12,
  fontWeight: 500,
  color: v('--prog-primary', '#00C896') as string,
  textTransform: 'uppercase',
  letterSpacing: '0.05em',
};

const hostBioStyle: React.CSSProperties = {
  margin: 0,
  fontSize: 13,
  lineHeight: 1.6,
  color: '#4B5563',
  textAlign: 'left',
};

const hostMetaStyle: React.CSSProperties = {
  borderTop: '1px solid #F3F4F6',
  paddingTop: 16,
  display: 'flex',
  flexDirection: 'column',
  gap: 10,
  textAlign: 'left',
};

const hostMetaRowStyle: React.CSSProperties = {
  display: 'flex',
  alignItems: 'center',
  gap: 8,
};

const hostMetaIconStyle: React.CSSProperties = {
  fontSize: 14,
  lineHeight: 1,
  opacity: 0.7,
};

const hostMetaLabelStyle: React.CSSProperties = {
  fontSize: 12.5,
  color: '#6B7280',
  fontWeight: 500,
};

const calendarWrapStyle: React.CSSProperties = {
  flex: 1,
  minWidth: 280,
  background: '#ffffff',
  borderRadius: v('--radius-card', '22px') as string,
  overflow: 'hidden',
  boxShadow: '0 2px 8px rgba(0,0,0,0.06), 0 8px 24px rgba(0,0,0,0.04)',
  minHeight: 660,
};

const calPlaceholderStyle: React.CSSProperties = {
  display: 'flex',
  alignItems: 'center',
  justifyContent: 'center',
  minHeight: 300,
  padding: 32,
};

const calPlaceholderTextStyle: React.CSSProperties = {
  fontSize: 14,
  color: '#9CA3AF',
  textAlign: 'center',
  lineHeight: 1.7,
};

const expectsSection: React.CSSProperties = {
  background: v('--prog-tint', '#E6F9F4') as string,
  borderRadius: v('--radius-container', '24px') as string,
  padding: '36px 32px',
  marginBottom: 40,
};

const sectionHeadingStyle: React.CSSProperties = {
  margin: '0 0 24px',
  fontSize: 20,
  fontWeight: 700,
  color: v('--prog-text-dark', '#111827') as string,
  fontFamily: v('--font-heading', "'Poppins', sans-serif") as string,
};

const expectsListStyle: React.CSSProperties = {
  margin: 0,
  padding: 0,
  listStyle: 'none',
  display: 'flex',
  flexDirection: 'column',
  gap: 16,
};

const expectsItemStyle: React.CSSProperties = {
  display: 'flex',
  alignItems: 'flex-start',
  gap: 14,
};

const expectsNumberStyle: React.CSSProperties = {
  flexShrink: 0,
  width: 28,
  height: 28,
  borderRadius: '50%',
  background: v('--prog-primary', '#00C896') as string,
  color: '#ffffff',
  display: 'flex',
  alignItems: 'center',
  justifyContent: 'center',
  fontSize: 13,
  fontWeight: 700,
  lineHeight: '1',
};

const expectsTextStyle: React.CSSProperties = {
  fontSize: 15,
  lineHeight: 1.6,
  color: v('--prog-text-dark', '#111827') as string,
  paddingTop: 4,
};

const quoteSection: React.CSSProperties = {
  textAlign: 'center',
  padding: '40px 24px',
  marginBottom: 40,
  background: '#ffffff',
  borderRadius: v('--radius-container', '24px') as string,
  boxShadow: '0 1px 3px rgba(0,0,0,0.04)',
};

const quoteStyle: React.CSSProperties = {
  margin: '0 0 12px',
  fontSize: 'clamp(18px, 3vw, 22px)',
  fontStyle: 'italic',
  lineHeight: 1.55,
  color: v('--prog-deep', '#003D32') as string,
  fontFamily: v('--font-display', "'Fraunces', serif") as string,
};

const quoteMarkStyle: React.CSSProperties = {
  color: v('--prog-primary', '#00C896') as string,
  fontStyle: 'normal',
  fontSize: '1.2em',
  lineHeight: 0,
  verticalAlign: '-0.2em',
  margin: '0 2px',
};

const quoteAttrStyle: React.CSSProperties = {
  margin: 0,
  fontSize: 13,
  fontWeight: 600,
  color: '#6B7280',
  letterSpacing: '0.03em',
  textTransform: 'uppercase',
};

const enrollSection: React.CSSProperties = {
  textAlign: 'center',
  padding: '32px 24px',
  background: v('--prog-tint', '#E6F9F4') as string,
  borderRadius: v('--radius-container', '24px') as string,
};

const enrollTextStyle: React.CSSProperties = {
  margin: '0 0 12px',
  fontSize: 14,
  color: '#6B7280',
};

const enrollCtaStyle: React.CSSProperties = {
  display: 'inline-block',
  background: v('--prog-primary', '#00C896') as string,
  color: '#ffffff',
  padding: '12px 24px',
  borderRadius: v('--radius-button', '8px') as string,
  textDecoration: 'none',
  fontSize: 14,
  fontWeight: 600,
  letterSpacing: '-0.01em',
  fontFamily: v('--font-heading', "'Poppins', sans-serif") as string,
};

const loadingStyle: React.CSSProperties = {
  display: 'flex',
  alignItems: 'center',
  justifyContent: 'center',
  minHeight: '60vh',
};

const spinnerStyle: React.CSSProperties = {
  width: 36,
  height: 36,
  borderRadius: '50%',
  border: `3px solid ${v('--prog-tint', '#E6F9F4') as string}`,
  borderTopColor: v('--prog-primary', '#00C896') as string,
  animation: 'spin 0.8s linear infinite',
};

const errorWrapStyle: React.CSSProperties = {
  display: 'flex',
  alignItems: 'center',
  justifyContent: 'center',
  minHeight: '60vh',
  padding: '40px 24px',
};

const errorCardStyle: React.CSSProperties = {
  background: '#ffffff',
  borderRadius: 22,
  padding: '48px 36px',
  maxWidth: 480,
  width: '100%',
  textAlign: 'center',
  boxShadow: '0 2px 8px rgba(0,0,0,0.06), 0 12px 32px rgba(0,0,0,0.06)',
};

const errorTitleStyle: React.CSSProperties = {
  margin: '0 0 10px',
  fontSize: 22,
  fontWeight: 700,
  color: '#111827',
};

const errorBodyStyle: React.CSSProperties = {
  margin: '0 0 24px',
  fontSize: 14,
  lineHeight: 1.6,
  color: '#6B7280',
};

const errorCtaStyle: React.CSSProperties = {
  display: 'inline-block',
  padding: '10px 22px',
  background: '#111827',
  color: '#ffffff',
  borderRadius: 8,
  textDecoration: 'none',
  fontSize: 14,
  fontWeight: 600,
};

const footerStyle: React.CSSProperties = {
  padding: '16px 24px',
  textAlign: 'center' as const,
  fontSize: 12,
  color: '#9CA3AF',
  borderTop: '1px solid rgba(0,0,0,0.06)',
};

const footLinkStyle: React.CSSProperties = {
  color: '#9CA3AF',
  textDecoration: 'none',
};
